import { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Mail, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = { sent: "Neu", in_progress: "In Arbeit", resolved: "Gelöst", closed: "Geschlossen" };
const STATUS_COLOR = { sent: "bg-[hsl(210_30%_50%/0.2)] text-[hsl(210_30%_75%)]", in_progress: "bg-[hsl(43_74%_49%/0.15)] text-[hsl(43_74%_66%)]", resolved: "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)]", closed: "bg-secondary text-muted-foreground" };

export default function AdminSupport() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const qc = useQueryClient();

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["admin-tickets", statusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search) params.set("q", search);
      const r = await axios.get(`${API}/admin/tickets?${params}`, { withCredentials: true });
      return r.data;
    },
  });

  const patch = useMutation({
    mutationFn: async ({ id, patch }) => (await axios.patch(`${API}/admin/tickets/${id}`, patch, { withCredentials: true })).data,
    onSuccess: (data) => { setSelected(data); qc.invalidateQueries({ queryKey: ["admin-tickets"] }); toast.success("Ticket aktualisiert"); },
    onError: (e) => toast.error("Update fehlgeschlagen", { description: e?.response?.data?.detail }),
  });

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Support-Tickets" description="Übersicht aller eingegangenen Support-Anfragen." testid="admin-support-header" />

      <div className="card-surface p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <Input placeholder="Suchen: E-Mail, Betreff, Ticket-ID …" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="admin-tickets-search" />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="admin-tickets-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="sent">Neu</SelectItem>
            <SelectItem value="in_progress">In Arbeit</SelectItem>
            <SelectItem value="resolved">Gelöst</SelectItem>
            <SelectItem value="closed">Geschlossen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="card-surface p-0 overflow-hidden">
        <Table data-testid="admin-tickets-table">
          <TableHeader><TableRow>
            <TableHead>Ticket-ID</TableHead><TableHead>Nutzer</TableHead><TableHead>Betreff</TableHead>
            <TableHead>Status</TableHead><TableHead>Erstellt</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading && (<TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Lädt…</TableCell></TableRow>)}
            {!isLoading && tickets.length === 0 && (<TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Keine Tickets gefunden.</TableCell></TableRow>)}
            {tickets.map((t) => (
              <TableRow key={t.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => setSelected(t)} data-testid={`admin-ticket-row-${t.id}`}>
                <TableCell className="font-mono text-xs">{t.id}</TableCell>
                <TableCell><div className="text-sm">{t.name || "—"}</div><div className="text-xs text-muted-foreground">{t.email}</div></TableCell>
                <TableCell className="max-w-[300px] truncate">{t.subject}</TableCell>
                <TableCell><Badge className={`rounded-full ${STATUS_COLOR[t.status] || ""}`}>{STATUS_LABEL[t.status] || t.status}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(t.created_at).toLocaleString("de-DE")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-ticket-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-sm text-muted-foreground">{selected.id}</DialogTitle>
                <div className="font-heading text-lg">{selected.subject}</div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="uppercase tracking-wider text-muted-foreground">Nutzer</div><div className="mt-1">{selected.name || "—"} · {selected.email}</div></div>
                  <div><div className="uppercase tracking-wider text-muted-foreground">Reply-To</div><div className="mt-1">{selected.contact_email}</div></div>
                  <div><div className="uppercase tracking-wider text-muted-foreground">Erstellt</div><div className="mt-1">{new Date(selected.created_at).toLocaleString("de-DE")}</div></div>
                  <div><div className="uppercase tracking-wider text-muted-foreground">Zugestellt an</div><div className="mt-1">{selected.delivered_to}</div></div>
                </div>
                <div className="rounded-lg border border-border bg-secondary/40 p-3">
                  <div className="overline mb-2">Anliegen</div>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{selected.message}</div>
                </div>
                {selected.conversation?.length > 0 && (
                  <div>
                    <div className="overline mb-2">KI-Konversation</div>
                    <div className="space-y-2 max-h-[240px] overflow-y-auto">
                      {selected.conversation.map((m, i) => (
                        <div key={i} className={`text-xs p-2 rounded ${m.role === "user" ? "bg-primary/10" : "bg-secondary/60"}`}>
                          <span className="font-semibold uppercase mr-2">{m.role === "user" ? "Nutzer" : "KI"}</span>
                          <span className="whitespace-pre-wrap">{m.content}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Status</div>
                    <Select value={selected.status} onValueChange={(v) => patch.mutate({ id: selected.id, patch: { status: v } })}>
                      <SelectTrigger data-testid="admin-ticket-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <a href={`mailto:${selected.contact_email || selected.email}?subject=Re: [${selected.id}] ${selected.subject}`} className="inline-block">
                    <Button variant="outline" className="w-full rounded-full" data-testid="admin-ticket-reply">
                      <Mail className="mr-2 h-4 w-4" strokeWidth={1.5} /> Per E-Mail antworten
                    </Button>
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
